/**
 * Demo dataset for ClinicalWorkspace — used by the /design/body-concepts gallery.
 * Kept OUTSIDE render so it doesn't recreate on every mount.
 */

export const DEMO_CLIENT = { name: "Ami Knecht", sex: "f" };

export const DEMO_SESSION_META = { num: 5, totalSessions: 12, date: "Feb 6, 2026" };

export const DEMO_STATS = {
  pain:    { current: 4,  intake: 8,  delta: -4 },
  rom:     { current: 78, intake: 42, delta: +36 },
  posture: { current: 84, intake: 58, delta: +26 },
};

export const DEMO_TREATMENT_MAP = {
  primary:   ["upper_trap", "rhomboids", "occiput"],
  secondary: ["pec_minor", "scm"],
  related:   ["erector_t", "hip_flexor", "ql"],
};

export const DEMO_TREATMENT_STEPS = [
  { num: 1, key: "occiput",    label: "Occipitals / Suboccipitals", time: "8 min", goal: "Decompress, reduce referral",   level: "primary"   },
  { num: 2, key: "pec_minor",  label: "Pec Minor",                  time: "6 min", goal: "Open anterior chain",            level: "secondary" },
  { num: 3, key: "upper_trap", label: "Upper Trapezius",            time: "7 min", goal: "Release tension, improve range", level: "primary"   },
  { num: 4, key: "rhomboids",  label: "Rhomboids",                  time: "6 min", goal: "Restore posterior balance",      level: "primary"   },
  { num: 5, key: "erector_t",  label: "Thoracic / Ribs",            time: "6 min", goal: "Improve thoracic mobility",      level: "related"   },
  { num: 6, key: "_reassess",  label: "Reassess & Integrate",       time: "5 min", goal: "ROM, posture, symptoms",         level: "complete"  },
];

export const DEMO_RATIONALE =
  "Releasing the neck first reduces protective guarding. Anterior chain (pec minor) before posterior (rhomboids) so the scapula resets before strengthening cues.";

export const DEMO_EXPECTED =
  "Improved neck rotation and reduced headache tension. Targeted release of upper-cross chain before posterior reset.";

export const DEMO_FINDINGS = {
  upper_trap: {
    label: "Upper Trapezius / Levator", level: "primary", confidence: 0.92, treatmentPriority: 1,
    why: "Forward head posture + elevated shoulder pattern detected. Pain referral matches client's chief complaint (right neck → occiput).",
    sequence: ["Suboccipital release", "Trapezius cross-fiber", "Levator stretch + reset"],
    expectedOutcome: "Reduce pain 2–3 pts · Restore +10–15° contralateral rotation",
    history: [9, 8, 7, 5, 3.5],
  },
  rhomboids: {
    label: "Rhomboids", level: "primary", confidence: 0.84, treatmentPriority: 4,
    why: "Anterior chain dominance is pulling the scapula into protraction. Rhomboids show length-tension failure.",
    sequence: ["Inter-scapular release", "Scapular retraction cue"],
    expectedOutcome: "Restore scapular position · +20% strength endurance",
    history: [8, 7.5, 7, 5.5, 4],
  },
  pec_minor: {
    label: "Pec Minor", level: "secondary", confidence: 0.78, treatmentPriority: 2,
    why: "Compensation for upper-cross tension. Tight pec minor is pulling scapula anterior/inferior.",
    sequence: ["Coracoid release", "Doorway stretch homework"],
    expectedOutcome: "Open anterior chain · Reduce shoulder elevation by 8°",
    history: [7, 6.5, 5.5, 4.5, 3],
  },
  scm: {
    label: "Scalene / SCM", level: "secondary", confidence: 0.71, treatmentPriority: 3,
    why: "Neck stability and breathing pattern overlap. Often overlooked but limits cervical rotation.",
    sequence: ["Gentle SCM release", "Diaphragmatic breathing reset"],
    expectedOutcome: "Improve cervical rotation · Quiet breathing pattern",
    history: [6, 5.5, 5, 4.5, 4],
    detail: { perSession: [
      { num: 3, pain: 5, rom: 50, note: "Holding stable — secondary priority" },
      { num: 4, pain: 4, rom: 60, note: "Breathing pattern smoother" },
      { num: 5, pain: 4, rom: 72, note: "Stable — to monitor" },
    ]},
  },
  erector_t: {
    label: "Erector Spinae (mid-thoracic)", level: "related", confidence: 0.65, treatmentPriority: 5,
    why: "Compensatory guarding from chronic upper-cross tension — likely to ease as the primary chain releases.",
    sequence: ["Mid-thoracic broad strokes", "Cat-cow mobility homework"],
    expectedOutcome: "Settle compensatory holding",
    history: [6, 5, 4, 3.5, 2.5],
  },
  hip_flexor: {
    label: "Hip Flexors", level: "related", confidence: 0.62, treatmentPriority: 6,
    why: "Anterior pelvic tilt detected in lateral posture photo. Driving compensatory pattern but secondary.",
    sequence: ["Psoas / iliacus release", "Hip flexor mobility homework"],
    expectedOutcome: "Improve standing posture score · Reduce lumbar load",
    history: [5, 5, 4.5, 4, 3.5],
  },
  occiput: {
    label: "Occipitals / Suboccipitals", level: "primary", confidence: 0.88, treatmentPriority: 1,
    why: "Suboccipital tension is the cleanest entry point for upper-cross release — reduces protective guarding throughout the chain.",
    sequence: ["Suboccipital traction", "Cervical neutral cue"],
    expectedOutcome: "Decompress · Reduce referral pattern",
    history: [8.5, 7.5, 6.5, 5, 3.5],
  },
  ql: {
    label: "Quadratus Lumborum", level: "related", confidence: 0.60,
    why: "Lateral chain compensation for forward head posture. Right side guarding noted.",
    sequence: ["QL release (side-lying)", "Hip hike cue"],
    expectedOutcome: "Reduce lateral tension",
    history: [5, 5, 4.5, 4, 3.5],
  },
};

// Per-session detail used by the Previous Session Findings rail
const PER_SESSION = {
  occiput:     [
    { num: 1, pain: 9, rom: 28, note: "Initial — severe occipital referral" },
    { num: 2, pain: 8, rom: 35, note: "Suboccipital release tolerated well" },
    { num: 3, pain: 7, rom: 45, note: "Cervical Rotation 35° → 45°" },
    { num: 4, pain: 5, rom: 62, note: "Upper Trap restriction improved" },
    { num: 5, pain: 4, rom: 78, note: "Pain 7/10 → 4/10 · Rotation 62° → 78°" },
  ],
  upper_trap:  [
    { num: 1, pain: 8, rom: 32, note: "Initial — bilateral upper trap rigidity" },
    { num: 2, pain: 7, rom: 40, note: "Cross-fiber tolerated, broad strokes" },
    { num: 3, pain: 7, rom: 45, note: "Right side more guarded than left" },
    { num: 4, pain: 5, rom: 62, note: "Rotation Lt 62° (was 45°)" },
    { num: 5, pain: 4, rom: 75, note: "Upper Trap restriction improved" },
  ],
  pec_minor:   [
    { num: 1, pain: 7, rom: 35, note: "New finding — coracoid tender" },
    { num: 2, pain: 6, rom: 45, note: "Released coracoid · doorway stretch RX" },
    { num: 3, pain: 5, rom: 55, note: "Shoulder elevation 14° → 10°" },
    { num: 4, pain: 4, rom: 65, note: "Anterior chain opening" },
    { num: 5, pain: 3, rom: 75, note: "Shoulder elevation 10° → 6°" },
  ],
  scm: [
    { num: 1, pain: 6, rom: 30, note: "Bilateral · breathing pattern flagged" },
    { num: 2, pain: 5, rom: 38, note: "Gentle release · diaphragmatic cue" },
    { num: 3, pain: 5, rom: 50, note: "Holding stable — secondary priority" },
    { num: 4, pain: 4, rom: 60, note: "Breathing pattern smoother" },
    { num: 5, pain: 4, rom: 72, note: "Stable — to monitor" },
  ],
  erector_t: [
    { num: 1, pain: 6, rom: null, note: "Compensatory guarding for upper-cross" },
    { num: 2, pain: 5, rom: null, note: "Mid-thoracic broad strokes" },
    { num: 3, pain: 4, rom: null, note: "Easing as upper chain releases" },
    { num: 4, pain: 3.5, rom: null, note: "Cat-cow homework adherent" },
    { num: 5, pain: 2.5, rom: null, note: "Settled — no direct work needed" },
  ],
  hip_flexor: [
    { num: 1, pain: 5, rom: null, note: "Anterior pelvic tilt detected" },
    { num: 2, pain: 5, rom: null, note: "Psoas release · mobility RX" },
    { num: 3, pain: 4.5, rom: null, note: "Standing posture improved" },
    { num: 4, pain: 4, rom: null, note: "Posture score 71% (was 58%)" },
    { num: 5, pain: 3.5, rom: null, note: "Posture score 84% — keep monitoring" },
  ],
};

export const DEMO_PREVIOUS_FINDINGS = ["occiput", "upper_trap", "pec_minor", "scm", "erector_t", "hip_flexor"].map((id) => ({
  id,
  label: DEMO_FINDINGS[id]?.label || id,
  history: DEMO_FINDINGS[id]?.history || [],
  detail: { perSession: PER_SESSION[id] || [] },
}));

export const DEMO_NEW_SINCE_LAST_VISIT = {
  improved: ["Upper Trap restriction (R) → mobile", "Occipital referral → quieter", "Posture score 71% → 84%"],
  stable:   ["Scalene / SCM tension holding steady", "Hip flexor mild guarding"],
  worse:    ["Left Pec Minor restriction newly flagged"],
  clientReported: ["Sleeping better this week", "Slight tension after long drive Friday"],
  romChanges: ["Cervical rotation L: 62° → 78°", "Shoulder elevation: 10° → 6°"],
};

export const DEMO_TREATMENT_LOGIC = {
  primaryComplaint: "Right-sided neck pain · referral to occiput",
  chronicPriority:  "Forward head posture · upper-cross syndrome",
  newFinding:       "Left pec minor restriction",
  compensation:     "Right QL guarding · anterior pelvic tilt",
};

export const DEMO_LEARNING_LIBRARY = {
  upper_trap: {
    label: "Upper Trapezius (pars descendens)", latin: "m. trapezius",
    origin: "External occipital protuberance, nuchal ligament, C7",
    insertion: "Lateral 1/3 of clavicle, acromion",
    action: "Elevates scapula · Upward rotation",
    innervation: "Spinal accessory n. (CN XI) + C3–C4",
    referral: "Trigger points refer to the temple, posterior auricle, and occiput — often misdiagnosed as tension headache.",
    relatedMuscles: ["Levator scapulae", "Rhomboids", "Pec minor", "Suboccipitals", "SCM"],
  },
};

export const DEMO_CLIENT_PLAIN_STEPS = [
  { icon: "🌿", label: "Release neck tension",   desc: "Reduce that achy feeling at the base of your skull", key: "occiput" },
  { icon: "🌬️", label: "Open the chest",         desc: "Help your shoulders fall back into a relaxed position", key: "pec_minor" },
  { icon: "🪷", label: "Reset the shoulders",    desc: "Build back your natural posture", key: "upper_trap" },
  { icon: "✨", label: "Reassess together",      desc: "Check in: how does it feel now?", key: "" },
];
